import { Check, Copy, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

/** One rule of the app's own stylesheet, formatted for reading. */
interface StyleRule {
  /** What the search matches: the selector (or at-rule) and its declarations. */
  key: string;
  text: string;
}

const MAX_SHOWN = 60;

/** `a { b: c; d: e; }` → one declaration per line, nested rules (inside @media) indented. */
function pretty(rule: CSSRule, indent = ''): string {
  if (rule instanceof CSSStyleRule) {
    const decls = rule.style.cssText
      .split(/;\s*(?=[-a-z]+\s*:|$)/i)
      .map((d) => d.trim())
      .filter(Boolean);
    return `${indent}${rule.selectorText} {\n${decls.map((d) => `${indent}  ${d};`).join('\n')}\n${indent}}`;
  }
  if (rule instanceof CSSGroupingRule) {
    const head = rule.cssText.slice(0, rule.cssText.indexOf('{')).trim();
    return `${indent}${head} {\n${Array.from(rule.cssRules, (r) => pretty(r, indent + '  ')).join('\n')}\n${indent}}`;
  }
  return indent + rule.cssText;
}

/** The app's built stylesheet (not the custom CSS, not fonts), read from the page itself so it's always current. */
function appRules(): StyleRule[] {
  const out: StyleRule[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    if (!sheet.href || !sheet.href.includes('/assets/')) continue;
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    for (const r of Array.from(rules)) {
      if (r instanceof CSSFontFaceRule) continue;
      const text = pretty(r);
      out.push({ key: text.toLowerCase(), text });
    }
  }
  return out;
}

/**
 * Admin → Server settings → Custom styling (#438): the app's current styles, searchable, so an admin can find the rule
 * behind something on screen and copy it into their own CSS to change. Their box keeps only their changes, so later
 * app updates still apply everywhere else.
 */
export function CssViewer({ areas, onAdd }: { areas: { id: string; label: string }[]; onAdd: (area: string, css: string) => void }) {
  const rules = useMemo(appRules, []);
  const [q, setQ] = useState('');
  const [area, setArea] = useState(areas[0]?.id ?? '');
  const [done, setDone] = useState<string>('');

  const term = q.trim().toLowerCase();
  const matches = term ? rules.filter((r) => r.key.includes(term)) : [];

  const flash = (id: string) => {
    setDone(id);
    setTimeout(() => setDone((d) => (d === id ? '' : d)), 1500);
  };
  const copy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      flash(id);
    } catch {
      /* clipboard blocked: "Add to" still works */
    }
  };

  return (
    <details className="css-viewer">
      <summary>Current app styles</summary>
      <div className="stack">
        <p className="muted small">
          Search for what you want to change: a class like <code>.bottomnav</code>, a color variable like <code>--primary</code>, or a property like{' '}
          <code>border-radius</code>. Add a rule to your CSS, then change it there. Only what you change needs to stay in your box.
        </p>
        <div className="form-row">
          <label className="grow">
            Search the app’s styles
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. .bottomnav or --primary" spellCheck={false} />
          </label>
          <label>
            Add to
            <select value={area} onChange={(e) => setArea(e.target.value)}>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {!term ? (
          <p className="muted small">{rules.length.toLocaleString()} rules in the app’s stylesheet.</p>
        ) : !matches.length ? (
          <p className="muted small">No rules match “{q.trim()}”.</p>
        ) : (
          <>
            <p className="muted small">
              {matches.length} {matches.length === 1 ? 'rule matches' : 'rules match'}
              {matches.length > MAX_SHOWN ? `, showing the first ${MAX_SHOWN}. Search for something more specific to see the rest.` : '.'}
            </p>
            <ul className="css-rules">
              {matches.slice(0, MAX_SHOWN).map((r, i) => {
                const id = `${i}:${r.text.length}`;
                return (
                  <li key={id}>
                    <pre className="mono">{r.text}</pre>
                    <div className="css-rule-actions">
                      <button
                        className="btn small"
                        onClick={() => {
                          onAdd(area, r.text);
                          flash('add' + id);
                        }}
                      >
                        {done === 'add' + id ? <Check size={14} /> : <Plus size={14} />} Add to my CSS
                      </button>
                      <button className="icon-btn" onClick={() => copy(r.text, id)} aria-label="Copy rule">
                        {done === id ? <Check size={15} /> : <Copy size={15} />}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </details>
  );
}
